import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Button, { ButtonStyle } from '../../common/components/Button/Button';
import ButtonContainer from '../../common/components/Button/ButtonContainer';
import Container from '../../common/components/Container/Container';
import TitleContainer from '../../common/components/TitleContainer/TitleContainer';
import { FlowConfigContext } from '../../providers/FlowConfigContext';
import FeePanel from '../../common/components/FeePanel/FeePanel';
import SVGInfo from '../../common/assets/svg/icons/SVGInfo';
import Loading from '../../common/components/Loading/Loading';
import { Action } from '../../config';
import { EventPayload } from '../../events';
import withRouteAnalytics from '../../services/analytics/withRouteAnalytics';
import { Screens } from '../../services/analytics/screens';

function SignTransactionScreen({ preApprovedTxMode = false }): JSX.Element {
  const { networkName, action, userSignedTransactionComplete, flowType } = useContext(FlowConfigContext);
  const { t } = useTranslation();
  const { state } = useLocation();

  const confirmButtonTapped = () => {
    userSignedTransactionComplete(state as EventPayload);
  };

  useEffect(() => {
    // Automatically confirm the issuance or refresh action
    if (action === Action.ISSUANCE || action === Action.REFRESH) {
      confirmButtonTapped();
    }
  }, [action]);

  const labelPath = preApprovedTxMode ? 'screens.signTransaction.preApprovedTx' : 'screens.signTransaction';

  const renderFeePanel = () => (
    <React.Suspense
      fallback={
        <div className="my-8">
          <Loading />
        </div>
      }
    >
      <FeePanel />
      <div className="-mt-1 flex w-full transform flex-row items-center justify-center rounded-xl border border-border p-4 mb-5">
        <SVGInfo />
        <div className="w-full text-left pl-2 leading-tight font-extralight text-sm">
          <span className="mt-2 break-normal">
            {t('screens.signTransaction.approveTransaction', { context: networkName })}
          </span>
        </div>
      </div>
    </React.Suspense>
  );

  const renderTitleContainer = () => (
    <TitleContainer center>
      <h1 data-testid={`${labelPath}.title`} className="mt-2">
        {t(`${labelPath}.title`, { context: networkName, flowType: t(`${flowType}`) })}
      </h1>
    </TitleContainer>
  );

  const renderButton = () => (
    <ButtonContainer>
      <Button
        style={ButtonStyle.DEFAULT}
        disabled={false}
        loading={false}
        dataTestid="OK_BUTTON"
        onClick={confirmButtonTapped}
      >
        <span>{t('screens.signTransaction.buttonLabel', { context: networkName })}</span>
      </Button>
    </ButtonContainer>
  );

  const renderContent = () => {
    switch (action) {
      case Action.ISSUANCE:
      case Action.REFRESH:
        return <Loading key="loading" />;
      case Action.SIGN_TRANSACTION:
        return (
          <React.Fragment key="sign-transaction">
            {renderTitleContainer()}
            {renderFeePanel()}
            <Loading key="loading" />
          </React.Fragment>
        );
      case Action.RESUME_TRANSACTION_SIGNING:
        return (
          <React.Fragment key="resume-transaction-signing">
            {renderTitleContainer()}
            {renderFeePanel()}
            {renderButton()}
          </React.Fragment>
        );
      default:
        return <></>;
    }
  };

  return <Container>{renderContent()}</Container>;
}

export default withRouteAnalytics(SignTransactionScreen, Screens.SIGN_TXN);
